import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { DialFace } from '../dial/Dial';
import { RevealScreen } from '../local/LocalGame';
import { ScoreList, type RevealRow } from '../ui/reveal';
import { Toast, useAnnounce, useFocusOnChange } from '../ui/shell';
import { RoomClient, clearToken, errorText, hasToken, storedName, type ConnStatus } from '../net/socket';
import {
  MIN_PLAYERS_REMOTE,
  NAME_MAX,
  type ErrorCode,
  type RoomView,
  sanitizeText,
} from '@shared/protocol';
import { dial10, fmtScore, initialsFor, plural } from '@shared/scoring';
import { DECKS, type DeckId } from '@shared/content';

/**
 * Remote play. Everyone on their own phone, joined by a four-character code.
 * The client renders exactly what the server says it may see — the secrecy is
 * enforced by the server, not by the furniture.
 */
export function RemoteGame({
  joinCode,
  deck,
  onExit,
  onNewRoom,
}: {
  joinCode: string | null;
  /** The mode chosen on the front door. Only the host's choice is used. */
  deck: DeckId;
  onExit: () => void;
  onNewRoom: () => void;
}) {
  const [status, setStatus] = useState<ConnStatus>({
    kind: 'naming',
    code: joinCode,
    error: null,
    suggestion: null,
  });
  const [view, setView] = useState<RoomView | null>(null);
  const [toast, setToastText] = useState<string | null>(null);
  const announce = useAnnounce();
  // Toasts are the only channel for server errors and host migration, and a
  // plain div is announced by nothing (SC 4.1.3). Everything visible here also
  // goes through the live region that has been mounted since first paint.
  const setToast = useCallback(
    (text: string) => {
      setToastText(text);
      announce(text);
    },
    [announce],
  );
  // Created during the first render, not in an effect: the name screen renders
  // before any effect runs, and a client captured as null there would make the
  // first tap of "Create the room" do nothing at all.
  const [client] = useState(
    () =>
      new RoomClient({
        onView: setView,
        onStatus: setStatus,
        onError: (code) => setToast(errorText(code)),
        onJoined: () => {},
      }),
  );

  useEffect(() => () => client.close(), [client]);

  // A reload mid-round must not ask who you are again: this browser already
  // holds a seat in that room, and the token restores it with its score and its
  // place in the rotation.
  useEffect(() => {
    const name = storedName();
    if (joinCode && name && hasToken(joinCode)) client.begin('join', name, joinCode);
    // Runs once, on mount, for the room named in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Announce the things a sighted player sees change on their own.
  const spoken = useRef('');
  useEffect(() => {
    if (!view) return;
    const giver = view.players.find((p) => p.id === view.giverId);
    let line = '';
    if (view.phase === 'lobby') line = `${view.players.length} players in the room.`;
    else if (view.phase === 'clue')
      line = view.you.isGiver ? 'Your turn to write the clue.' : `${giver?.name ?? 'Someone'} is writing a clue.`;
    else if (view.phase === 'guessing')
      line = view.you.isGiver
        ? `${view.lockedIn.length} of ${view.players.length - 1} locked in.`
        : `Your turn to guess. Clue: ${view.clue ?? ''}`;
    else if (view.phase === 'reveal') line = `Reveal. Target ${dial10(view.target ?? 0)}.`;
    else if (view.phase === 'final') line = 'Final scores.';
    if (line && line !== spoken.current) {
      spoken.current = line;
      announce(line);
    }
    if (view.hostChangedTo) setToast(`${view.hostChangedTo} is the host now.`);
  }, [view, announce]);

  if (status.kind === 'naming') {
    return (
      <NameScreen
        code={status.code}
        error={status.error}
        suggestion={status.suggestion}
        onBack={onExit}
        onSubmit={(name) => client.begin(status.code ? 'join' : 'create', name, status.code)}
      />
    );
  }

  if (status.kind === 'ended') {
    return <RoomEnded reason={status.reason} lastView={status.lastView} onExit={onExit} onNewRoom={onNewRoom} />;
  }

  if (status.kind === 'restarting' && !view) {
    return (
      <Centered
        eyebrow="Needle"
        heading="The server is restarting"
        sub="Your seat and score are held. This reconnects on its own in a few seconds."
        action={
          <button type="button" className="btn ghost" onClick={onExit}>
            Back to the start
          </button>
        }
      />
    );
  }

  if (status.kind === 'waitingForNextGame') {
    return (
      <Centered
        eyebrow={`Room ${status.code}`}
        heading="That game already started"
        sub="You'll be able to join when they finish this one. This screen checks every ten seconds."
        action={
          <button type="button" className="btn ghost" onClick={onExit}>
            Back to the start
          </button>
        }
      />
    );
  }

  if (!view) {
    return (
      <Centered
        eyebrow="Connecting"
        heading={status.kind === 'reconnecting' ? 'Reconnecting…' : 'Connecting…'}
        sub="One moment."
        action={
          <button type="button" className="btn ghost" onClick={onExit}>
            Cancel
          </button>
        }
      />
    );
  }

  const reconnecting = status.kind === 'reconnecting' || status.kind === 'restarting';

  return (
    <>
      {reconnecting && (
        <div className="banner">
          <span className="eyebrow">{status.kind === 'restarting' ? 'Restarting' : 'Offline'}</span>
          <span>
            {status.kind === 'restarting'
              ? 'The server is restarting — your seat and score are held.'
              : 'Reconnecting — your seat and score are held.'}
          </span>
        </div>
      )}
      <RoomScreens view={view} client={client} deck={deck} onExit={onExit} setToast={setToast} />
      <Toast text={toast} onDone={() => setToastText(null)} />
    </>
  );
}

function RoomScreens({
  view,
  client,
  deck,
  onExit,
  setToast,
}: {
  view: RoomView;
  client: RoomClient;
  deck: DeckId;
  onExit: () => void;
  setToast: (s: string) => void;
}) {
  const headingRef = useFocusOnChange<HTMLHeadingElement>(`${view.phase}:${view.round}:${view.you.hasGuessed}`);
  const giver = view.players.find((p) => p.id === view.giverId);
  const roundLabel = `Round ${view.round + 1} of ${view.totalRounds}`;

  switch (view.phase) {
    case 'lobby':
      return (
        <Lobby
          view={view}
          client={client}
          deck={deck}
          headingRef={headingRef}
          onExit={onExit}
          setToast={setToast}
        />
      );

    case 'clue':
      if (view.you.isGiver) {
        return <RemoteClue view={view} client={client} headingRef={headingRef} roundLabel={roundLabel} />;
      }
      return (
        <WaitingForClue view={view} client={client} headingRef={headingRef} roundLabel={roundLabel} giverName={giver?.name ?? '…'} />
      );

    case 'guessing':
      if (view.you.isGiver || view.you.hasGuessed) {
        return (
          <WaitingForGuesses view={view} client={client} headingRef={headingRef} roundLabel={roundLabel} />
        );
      }
      return <RemoteGuess view={view} client={client} headingRef={headingRef} roundLabel={roundLabel} giverName={giver?.name ?? '…'} />;

    case 'reveal': {
      const rows: RevealRow[] = (view.guesses ?? []).map((g) => {
        const p = view.players.find((x) => x.id === g.playerId);
        return {
          name: p?.name ?? '—',
          value: g.value,
          points: g.points,
          total: p?.score ?? 0,
          isYou: g.playerId === view.you.id,
        };
      });
      const hostName = view.players.find((p) => p.isHost)?.name ?? 'the host';
      return (
        <RevealScreen
          roundLabel={roundLabel}
          card={view.card ?? ['', '']}
          target={view.target ?? 0}
          clue={view.clue ?? ''}
          giverName={giver?.name ?? '—'}
          giverPoints={view.giverPoints ?? 0}
          giverTotal={giver?.score ?? 0}
          rows={rows}
          markers={(() => {
            const labels = initialsFor(view.players.map((p) => p.name));
            return (view.guesses ?? []).map((g) => {
              const idx = view.players.findIndex((p) => p.id === g.playerId);
              return {
                value: g.value,
                label: g.playerId === view.you.id ? 'YOU' : (labels[idx] ?? '?'),
              };
            });
          })()}
          headingRef={headingRef}
          isLast={view.round + 1 >= view.totalRounds}
          coop={
            view.players.length === 2
              ? { combined: view.players.reduce((a, p) => a + p.score, 0), possible: view.scoredRounds * 8 }
              : null
          }
          onNext={view.you.mayAdvance ? () => client.act({ t: 'next', round: view.round }) : undefined}
          onEnd={view.you.isHost ? () => client.act({ t: 'endGame', round: view.round }) : undefined}
          advanceLabel={
            view.you.isHost ? undefined : view.round + 1 >= view.totalRounds ? 'See final scores' : 'Next round'
          }
          waitingFor={hostName}
        />
      );
    }

    case 'final': {
      const ranked = view.players.slice().sort((a, b) => b.score - a.score);
      const top = ranked[0]?.score ?? 0;
      const won = ranked.filter((p) => p.score === top);
      const coop = view.players.length === 2;
      const possible = view.scoredRounds * 8;
      const combined = view.players.reduce((a, p) => a + p.score, 0);
      return (
        <section className="screen">
          <div className="bar">
            <span className="eyebrow">Final</span>
            <span className="eyebrow">Room {view.code}</span>
          </div>
          <div className="body-area">
            <div>
              <h1 className="final-h" tabIndex={-1} ref={headingRef}>
                {coop
                  ? `${fmtScore(combined)} of a possible ${possible}`
                  : won.length > 1
                    ? `${won.map((p) => p.name).join(' & ')} tie`
                    : `${won[0]?.name ?? '—'} wins`}
              </h1>
              <p className="final-sub">
                {coop ? 'Two players score identically every round — so this one was co-op.' : `${fmtScore(top)} points`}
              </p>
            </div>
            <ul className="scores">
              {ranked.map((p, i) => (
                <li key={p.id} className={!coop && i === 0 ? 'win' : undefined}>
                  <span className="tag">{coop ? '—' : `#${i + 1}`}</span>
                  <span className="who">
                    {p.name}
                    {p.id === view.you.id ? ' (you)' : ''}
                  </span>
                  <span className="pts">{fmtScore(p.score)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="actions">
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                clearToken(view.code);
                onExit();
              }}
            >
              Back to the start
            </button>
          </div>
        </section>
      );
    }

    default:
      return null;
  }
}

// ── screens ─────────────────────────────────────────────────────────────────

function NameScreen({
  code,
  error,
  suggestion,
  onSubmit,
  onBack,
}: {
  code: string | null;
  error: ErrorCode | null;
  suggestion: string | null;
  onSubmit: (name: string) => void;
  onBack: () => void;
}) {
  const [name, setName] = useState(() => suggestion ?? storedName());
  const headingRef = useFocusOnChange<HTMLHeadingElement>('naming');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const clean = sanitizeText(name).slice(0, NAME_MAX);

  useEffect(() => {
    if (suggestion) setName(suggestion);
  }, [suggestion]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [suggestion]);

  return (
    <section className="screen">
      <div className="bar">
        <button type="button" className="eyebrow linkish" onClick={onBack}>
          Back
        </button>
        <span className="eyebrow">{code ? `Joining room ${code}` : 'New room'}</span>
      </div>
      <form
        className="body-area"
        onSubmit={(e) => {
          e.preventDefault();
          if (clean) onSubmit(clean);
        }}
      >
        <h1 className="waiting-h" tabIndex={-1} ref={headingRef}>
          {code ? `Joining room ${code}` : 'Start a remote game'}
        </h1>
        <p className="waiting-sub">What should everyone call you?</p>
        <label className="visually-hidden" htmlFor="player-name">
          Your name
        </label>
        <input
          id="player-name"
          ref={inputRef}
          type="text"
          value={name}
          maxLength={NAME_MAX}
          autoComplete="nickname"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          placeholder="Your name"
          onChange={(e) => setName(e.target.value)}
        />
        {name.length >= NAME_MAX - 3 && (
          <p className="counter">
            {name.length} / {NAME_MAX}
          </p>
        )}
        {error && <p className="note err">{errorText(error)}</p>}
        <button type="submit" className="visually-hidden">
          Continue
        </button>
      </form>
      <div className="actions">
        <button type="button" className="btn" disabled={!clean} onClick={() => onSubmit(clean)}>
          {code ? 'Join the room' : 'Create the room'}
        </button>
      </div>
    </section>
  );
}

function Lobby({
  view,
  client,
  deck,
  headingRef,
  onExit,
  setToast,
}: {
  view: RoomView;
  client: RoomClient;
  deck: DeckId;
  headingRef: React.RefCallback<HTMLHeadingElement>;
  onExit: () => void;
  setToast: (s: string) => void;
}) {
  const [passes, setPasses] = useState<1 | 2>(view.players.length === 2 ? 2 : 1);
  // Seeded from the front door, changeable here — the host may only decide
  // which mode they want once everyone is in the room.
  const [chosenDeck, setChosenDeck] = useState<DeckId>(deck);
  const [qr, setQr] = useState<string | null>(null);
  const link = `${location.origin}/#/${view.code}`;
  const enough = view.players.length >= MIN_PLAYERS_REMOTE;

  useEffect(() => {
    let alive = true;
    // toDataURL, never toString({type:'svg'}): the SVG form returns a string
    // and rendering it would mean dangerouslySetInnerHTML. A data: URI in an
    // <img> is covered by `img-src 'self' data:` and injects nothing.
    QRCode.toDataURL(link, { margin: 1, width: 320, color: { dark: '#0F1620', light: '#E8DFCC' } })
      .then((url) => alive && setQr(url))
      .catch(() => alive && setQr(null));
    return () => {
      alive = false;
    };
  }, [link]);

  const share = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Needle', text: `Join my game of Needle — code ${view.code}`, url: link });
        return;
      }
      await navigator.clipboard.writeText(link);
      setToast('Link copied. Paste it to the group thread.');
    } catch {
      setToast('Copy failed — the code is on screen, read it out.');
    }
  }, [link, view.code, setToast]);

  return (
    <section className="screen">
      <div className="bar">
        <button type="button" className="eyebrow linkish" onClick={onExit}>
          Leave
        </button>
        <span className="eyebrow">{view.you.isHost ? 'You are the host' : 'Lobby'}</span>
      </div>
      <div className="body-area top">
        {view.you.isHost ? (
          <>
            <div>
              <span className="eyebrow code-label">Share code</span>
              <h1 className="code-hero" tabIndex={-1} ref={headingRef}>
                {view.code}
              </h1>
            </div>
            {qr && <img className="qr" src={qr} alt={`QR code linking to room ${view.code}`} width={168} height={168} />}
            <div className="share-row">
              <button type="button" className="btn ghost" onClick={share}>
                Copy link
              </button>
            </div>
          </>
        ) : (
          <div>
            <h1 className="waiting-h" tabIndex={-1} ref={headingRef}>
              You&rsquo;re in
            </h1>
            <p className="waiting-sub">
              Waiting for {view.players.find((p) => p.isHost)?.name ?? 'the host'} to start. Room {view.code}.
            </p>
          </div>
        )}

        <ul className="roster">
          {view.players.map((p) => (
            <li key={p.id}>
              <span className={`dot${p.online ? '' : ' off'}`} aria-hidden="true" />
              <span className="who">
                {p.name}
                {p.id === view.you.id ? ' (you)' : ''}
              </span>
              <span className="tag">{p.isHost ? 'Host' : p.online ? '' : 'Offline'}</span>
            </li>
          ))}
        </ul>

        {view.you.isHost && (
          <div className="field">
            <span className="eyebrow" id="lbl-mode">
              Mode
            </span>
            <div className="seg wide" role="group" aria-labelledby="lbl-mode">
              {(Object.keys(DECKS) as DeckId[]).map((id) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={chosenDeck === id}
                  onClick={() => setChosenDeck(id)}
                >
                  {DECKS[id].label}
                </button>
              ))}
            </div>
            <p className="note">{DECKS[chosenDeck].blurb}</p>
          </div>
        )}

        {view.you.isHost && (
          <div className="field">
            <span className="eyebrow" id="lbl-length">
              Game length
            </span>
            <div className="seg wide" role="group" aria-labelledby="lbl-length">
              <button type="button" aria-pressed={passes === 1} onClick={() => setPasses(1)}>
                One pass
              </button>
              <button type="button" aria-pressed={passes === 2} onClick={() => setPasses(2)}>
                Two passes
              </button>
            </div>
            <p className="note">
              {view.players.length === 2
                ? 'With two players you always score the same — it plays as co-op. Push the total as high as you can.'
                : `Everyone gives ${passes === 1 ? 'one clue' : 'two clues'} — ${plural(view.players.length * passes, 'round')}.`}
            </p>
          </div>
        )}
      </div>
      <div className="actions">
        {view.you.isHost ? (
          <button
            type="button"
            className="btn"
            disabled={!enough}
            onClick={() => client.send({ t: 'start', passes, deck: chosenDeck })}
          >
            {enough ? `Start with ${view.players.length} players` : 'Waiting for one more player'}
          </button>
        ) : (
          <button type="button" className="btn ghost" onClick={share}>
            Invite someone
          </button>
        )}
      </div>
    </section>
  );
}

function RemoteClue({
  view,
  client,
  headingRef,
  roundLabel,
}: {
  view: RoomView;
  client: RoomClient;
  headingRef: React.RefCallback<HTMLHeadingElement>;
  roundLabel: string;
}) {
  const [text, setText] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const clean = text.trim();

  useEffect(() => {
    setText('');
    setElapsed(0);
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [view.round]);

  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">{roundLabel}</span>
        <span className="eyebrow hot">
          {elapsed >= 45 ? `${elapsed}s — they're waiting` : 'You write the clue'}
        </span>
      </div>
      <div className="body-area">
        <h1 className="visually-hidden" tabIndex={-1} ref={headingRef}>
          Write your clue
        </h1>
        <p className="readout">
          <small>The hidden target — only you see this</small>
          {dial10(view.target ?? 50)}
        </p>
        <DialFace value={view.target ?? 50} poles={view.card} label="The hidden target" />
        <div>
          <label className="visually-hidden" htmlFor="clue-input">
            Your clue
          </label>
          <textarea
            id="clue-input"
            maxLength={60}
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write one clue that lands exactly on the target…"
          />
          <p className="counter">{text.length} / 60</p>
        </div>
      </div>
      <div className="actions">
        <button
          type="button"
          className="btn"
          disabled={!clean}
          onClick={() => client.act({ t: 'clue', round: view.round, text: clean })}
        >
          Lock in clue
        </button>
      </div>
    </section>
  );
}

function WaitingForClue({
  view,
  client,
  headingRef,
  roundLabel,
  giverName,
}: {
  view: RoomView;
  client: RoomClient;
  headingRef: React.RefCallback<HTMLHeadingElement>;
  roundLabel: string;
  giverName: string;
}) {
  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">{roundLabel}</span>
        <span className="eyebrow">Room {view.code}</span>
      </div>
      <div className="body-area top">
        <h1 className="waiting-h" tabIndex={-1} ref={headingRef}>
          {giverName} is writing a clue
        </h1>
        <div className="poles-pill">
          <span>{view.card?.[0]}</span>
          <i>vs</i>
          <span>{view.card?.[1]}</span>
        </div>
        {/* Dead air is where a remote party game dies. The previous round,
            resolved, is the argument — and it runs while the clue is being
            written instead of competing with it. Round one has no previous
            round, so it gets a prompt instead of a blank half-screen. */}
        {view.lastRound ? (
          <LastRound view={view} />
        ) : (
          <p className="waiting-sub">
            While you wait: what would a <b>5</b> be on this one? Everybody has a different answer and that is
            the whole game.
          </p>
        )}
        <Standings view={view} />
      </div>
      <div className="actions">
        <HostHatch view={view} client={client} />
      </div>
    </section>
  );
}

function RemoteGuess({
  view,
  client,
  headingRef,
  roundLabel,
  giverName,
}: {
  view: RoomView;
  client: RoomClient;
  headingRef: React.RefCallback<HTMLHeadingElement>;
  roundLabel: string;
  giverName: string;
}) {
  const [value, setValue] = useState(50);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setValue(50);
    setTouched(false);
  }, [view.round]);

  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">{roundLabel}</span>
        <span className="eyebrow hot">{giverName}&rsquo;s clue</span>
      </div>
      <div className="body-area">
        <h1 className="visually-hidden" tabIndex={-1} ref={headingRef}>
          Your guess
        </h1>
        <div className="cluecard">
          <span className="eyebrow">The clue</span>
          <p>{view.clue}</p>
        </div>
        <p className="readout">
          <small>Your guess</small>
          {dial10(value)}
        </p>
        <DialFace
          value={value}
          poles={view.card}
          onChange={(v) => {
            setValue(v);
            setTouched(true);
          }}
        />
        <p className="hint">
          {view.lockedIn.length > 0
            ? `${view.lockedIn.length} of ${view.players.length - 1} locked in`
            : 'Drag the needle'}
        </p>
      </div>
      <div className="actions">
        <button
          type="button"
          className="btn"
          disabled={!touched}
          onClick={() => client.act({ t: 'guess', round: view.round, value })}
        >
          {touched ? 'Lock in guess' : 'Move the needle first'}
        </button>
      </div>
    </section>
  );
}

function WaitingForGuesses({
  view,
  client,
  headingRef,
  roundLabel,
}: {
  view: RoomView;
  client: RoomClient;
  headingRef: React.RefCallback<HTMLHeadingElement>;
  roundLabel: string;
}) {
  const guessers = view.players.filter((p) => p.id !== view.giverId);
  const waiting = guessers.filter((p) => !view.lockedIn.includes(p.id));
  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">{roundLabel}</span>
        <span className="eyebrow hot">
          {view.lockedIn.length} of {guessers.length} locked in
        </span>
      </div>
      <div className="body-area top">
        <h1 className="waiting-h" tabIndex={-1} ref={headingRef}>
          {waiting.length === 0 ? 'Everyone’s in' : `Waiting on ${waiting.map((p) => p.name).join(', ')}`}
        </h1>
        <div className="cluecard">
          <span className="eyebrow">The clue</span>
          <p>{view.clue}</p>
        </div>
        <ul className="roster">
          {guessers.map((p) => (
            <li key={p.id}>
              <span
                className={`dot${!p.online ? ' off' : view.lockedIn.includes(p.id) ? ' done' : ''}`}
                aria-hidden="true"
              />
              <span className="who">
                {p.name}
                {p.id === view.you.id ? ' (you)' : ''}
              </span>
              {/* Who has locked in. Never what they said. */}
              <span className="tag">{view.lockedIn.includes(p.id) ? 'Locked in' : p.online ? 'Thinking' : 'Offline'}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="actions">
        <HostHatch view={view} client={client} />
      </div>
    </section>
  );
}

function HostHatch({ view, client }: { view: RoomView; client: RoomClient }) {
  if (!view.hostAction) return <p className="hint">The round moves on when everyone&rsquo;s in</p>;
  const { kind, names } = view.hostAction;
  return (
    <button
      type="button"
      className="btn ghost"
      onClick={() =>
        kind === 'skipRound'
          ? client.act({ t: 'skipRound', round: view.round })
          : client.act({ t: 'revealNow', round: view.round })
      }
    >
      {kind === 'skipRound' ? 'Skip this round' : `Reveal without ${names.join(', ')}`}
    </button>
  );
}

function LastRound({ view }: { view: RoomView }) {
  const last = view.lastRound!;
  const rows: RevealRow[] = last.guesses.map((g) => {
    const p = view.players.find((x) => x.id === g.playerId);
    return { name: p?.name ?? '—', value: g.value, points: g.points, total: p?.score ?? 0 };
  });
  return (
    // Open by default: a disclosure widget everyone has to independently decide
    // to tap is not a conversation starter.
    <details open>
      <summary>Last round · target {dial10(last.target)}</summary>
      <p className="waiting-sub">
        {last.card[0]} → {last.card[1]}
      </p>
      <ScoreList
        giverName={last.giverName}
        giverClue={last.clue}
        giverPoints={rows.length ? rows.reduce((a, r) => a + r.points, 0) / rows.length : 0}
        giverTotal={view.players.find((p) => p.name === last.giverName)?.score ?? 0}
        rows={rows}
      />
    </details>
  );
}

function Standings({ view }: { view: RoomView }) {
  const ranked = view.players.slice().sort((a, b) => b.score - a.score);
  return (
    <ul className="scores">
      {ranked.map((p) => (
        <li key={p.id}>
          <span className={`dot${p.online ? '' : ' off'}`} aria-hidden="true" />
          <span className="who">
            {p.name}
            {p.id === view.you.id ? ' (you)' : ''}
          </span>
          <span className="pts">{fmtScore(p.score)}</span>
        </li>
      ))}
    </ul>
  );
}

function RoomEnded({
  reason,
  lastView,
  onExit,
  onNewRoom,
}: {
  reason: 'kicked' | 'roomGone' | 'restarting' | 'replaced';
  lastView: RoomView | null;
  onExit: () => void;
  onNewRoom: () => void;
}) {
  const headingRef = useFocusOnChange<HTMLHeadingElement>(reason);
  const ranked = useMemo(
    () => (lastView ? lastView.players.slice().sort((a, b) => b.score - a.score) : []),
    [lastView],
  );

  const copy: Record<typeof reason, { h: string; sub: string }> = {
    kicked: { h: 'You left the room', sub: 'The host removed you from this game.' },
    roomGone: {
      h: 'Room ended',
      sub: 'This room is gone. Rooms don’t outlive a server restart, and they close after half an hour of quiet.',
    },
    restarting: {
      h: 'Room ending',
      sub: 'The game server is restarting. Rooms don’t survive a restart.',
    },
    replaced: { h: 'Opened somewhere else', sub: 'This room is now open in another tab or on another device.' },
  };

  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">Needle</span>
        <span className="eyebrow">{lastView?.code ?? ''}</span>
      </div>
      <div className="body-area top">
        <div>
          <h1 className="waiting-h" tabIndex={-1} ref={headingRef}>
            {copy[reason].h}
          </h1>
          <p className="waiting-sub">{copy[reason].sub}</p>
        </div>
        {ranked.length > 0 && (
          <>
            <span className="eyebrow">Where things stood</span>
            <ul className="scores">
              {ranked.map((p) => (
                <li key={p.id}>
                  <span className="who">
                    {p.name}
                    {p.id === lastView?.you.id ? ' (you)' : ''}
                  </span>
                  <span className="pts">{fmtScore(p.score)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <div className="actions">
        <button type="button" className="btn" onClick={onNewRoom}>
          Start a new game
        </button>
        <button type="button" className="btn ghost" onClick={onExit}>
          Back to the start
        </button>
      </div>
    </section>
  );
}

function Centered({
  eyebrow,
  heading,
  sub,
  action,
}: {
  eyebrow: string;
  heading: string;
  sub: string;
  action: React.ReactNode;
}) {
  const headingRef = useFocusOnChange<HTMLHeadingElement>(heading);
  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">{eyebrow}</span>
      </div>
      <div className="body-area">
        <h1 className="waiting-h" tabIndex={-1} ref={headingRef}>
          {heading}
        </h1>
        <p className="waiting-sub">{sub}</p>
      </div>
      <div className="actions">{action}</div>
    </section>
  );
}
